import PlaceholderIcon from '@streamlinehq/streamlinehq/img/streamline-light/culture-religion/greek-gods/greek-god-zeus-lightning.svg';
import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { getTextStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { desktop993pxOrWider } from '@upright/ui/common/generic/layout/breakpoint-utils';
import {
  detailsModalCommonHorizontalPadding,
  detailsModalCommonVerticalPadding,
} from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/common/styles';
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

interface Props {
  textAboveNumber?: ReactNode | null;
  textBelowNumber?: ReactNode;
  numberText?: ReactNode;
  explanationText?: ReactNode;
  icon?: any;
  highlighted?: boolean;
  testId?: E2eTestElementId;
}

/**
 * Plain icon figure box (without notes, but including explanation)
 */
export function PlainIconFigureBox(props: Props) {
  const { numberText, textAboveNumber, textBelowNumber, explanationText } = props;

  const padding = '1.15em';

  const iconStyle = {
    paddingTop: padding,
    paddingBottom: padding,
    paddingRight: padding,
    paddingLeft: padding,
    maxWidth: '12em',
    width: '100%',
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const Icon = props.icon || PlaceholderIcon;

  const showTextAboveNumber = textAboveNumber !== null;
  return (
    <Root data-testid={props.testId}>
      <NumberPart style={{ backgroundColor: props.highlighted ? BouColor.YELLOW : undefined }}>
        <Icon style={iconStyle} />
        <TextAboveNumber style={{ opacity: showTextAboveNumber ? 1 : 0 }}>
          {textAboveNumber || 'equivalent in size to'}
        </TextAboveNumber>
        <Number>{numberText}</Number>
        <TextBelowNumber>{textBelowNumber}</TextBelowNumber>
      </NumberPart>
      <ExplanationPart>{explanationText}</ExplanationPart>
    </Root>
  );
}

const Root = styled.div`
  ${getTextStyle({ size: 'xxs' })}
  line-height: 1.0;
  & + & {
    border-left: 1px solid ${BouColor.TOOL_BORDER_LIGHTER_GRAY};
  }
`;

const NumberPart = styled.div`
  ${detailsModalCommonVerticalPadding};
  ${detailsModalCommonHorizontalPadding};
  text-align: center;
  ${desktop993pxOrWider(css`
    padding-top: 3em;
  `)};
`;

const ExplanationPart = styled.div`
  ${detailsModalCommonVerticalPadding};
  ${detailsModalCommonHorizontalPadding};
  font-size: 1em;
  border-top: 1px solid ${BouColor.TOOL_BORDER_LIGHTER_GRAY};
  b {
    font-weight: 500;
  }
  line-height: 1.3;
`;

const TextAboveNumber = styled.div`
  font-size: 1.2em;
  padding-bottom: 0.3em;
  font-weight: 400;
  display: none;
`;

const TextBelowNumber = styled.div`
  font-size: 1.2em;
  padding-top: 0.3em;
  padding-bottom: 0.1em;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const Number = styled.div`
  font-size: 2em;
  font-weight: 500;
`;
